import {css, view} from '@sail/ui/deprecated';
import {thumbsUp, thumbsDown} from '@sail/icons/react/Icon';
import {useCallback, useEffect, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {CloseButton} from '../CloseButton';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {OptionButton} from './OptionButton';
import {useApi} from '../../../Providers/ApiProvider/ApiProvider';
import {DidWeResolveOption} from '../../../Providers/ApiProvider/api/feedback';

export const messages = defineMessages({
  formTitle: {
    id: 'support_conversations_client.supportWidget.didWeSolve.title',
    description: 'Title shown in the did we solve form',
    defaultMessage: 'Before you go, did we solve your issue?',
  },
  yesButtonText: {
    id: 'support_conversations_client.supportWidget.didWeSolve.yesButtonText',
    description: 'Label for "Yes" button in did we solve form',
    defaultMessage: 'Yes',
  },
  noButtonText: {
    id: 'support_conversations_client.supportWidget.didWeSolve.noButtonText',
    description: 'Label for "No" button in did we solve form',
    defaultMessage: 'No',
  },
  cancelButtonText: {
    id: 'support_conversations_client.supportWidget.didWeSolve.cancelButtonText',
    description:
      'Text shown for the cancel button when on the did we solve form',
    defaultMessage: 'Cancel',
  },
});

type Props = {
  conversationId: string;
  onDone: (option: DidWeResolveOption) => void;
};

export function DidWeSolveForm({conversationId, onDone}: Props) {
  const intl = useIntl();
  const analytics = useClientAnalytics();
  const {submitFeedback} = useApi();
  const [submissionPending, setSubmissionPending] = useState(false);
  const isFirstRenderRef = useRef<boolean>(false);

  const handleCancel = useCallback(() => {
    analytics.action('support_conversations_did_we_solve_cancel');
    onDone('no_answer');
  }, [analytics, onDone]);

  const handleSubmit = useCallback(
    async (didWeResolve: DidWeResolveOption) => {
      if (submissionPending) {
        return;
      }

      setSubmissionPending(true);
      analytics.action('support_conversations_did_we_solve_submit', {
        did_we_resolve: didWeResolve,
      });

      try {
        await submitFeedback({
          conversationId,
          didWeResolve,
        });
      } catch (e: any) {
        analytics.track('support_conversations_did_we_solve_error');
      } finally {
        setSubmissionPending(false);
        onDone(didWeResolve);
      }
    },
    [submissionPending, submitFeedback, analytics, conversationId, onDone],
  );

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      isFirstRenderRef.current = true;
      analytics.track('support_conversations_did_we_solve_impression');
    }
  }, [analytics]);

  return (
    <view.div
      data-testid="support-widget-did-we-solve-form"
      css={{position: css.value('relative')}}
    >
      <view.form onSubmit={(e) => e.preventDefault()}>
        <view.h2
          css={{
            margin: 0,
            paddingTop: 400,
            marginRight: 'medium',
            fontSize: css.value('24px'),
            lineHeight: css.value(1.2),
            fontWeight: 'bold',
          }}
        >
          {intl.formatMessage(messages.formTitle)}
        </view.h2>

        <view.div css={{marginTop: 'large', stack: 'x', gap: 'medium'}}>
          <OptionButton
            onPress={() => handleSubmit('resolved')}
            disabled={submissionPending}
            icon={thumbsUp}
            dataTestId="support-widget-did-we-solve-form-yes-button"
          >
            {intl.formatMessage(messages.yesButtonText)}
          </OptionButton>
          <OptionButton
            onPress={() => handleSubmit('not_resolved')}
            disabled={submissionPending}
            icon={thumbsDown}
            dataTestId="support-widget-did-we-solve-form-no-button"
          >
            {intl.formatMessage(messages.noButtonText)}
          </OptionButton>
        </view.div>
        <CloseButton
          onClick={handleCancel}
          dataTestId="support-widget-did-we-solve-cancel-button"
        />
      </view.form>
    </view.div>
  );
}
